import { create } from 'zustand'
import { GroupModel } from '../models/GroupModel'
import { TemplateModel } from '../models/TemplateModel'
import { UserModel } from '../models/UserModel'
import { EncounterModel } from '../models/EncounterModel'
import { OrganizationModel } from '../models/OrganizationModel'
import { PatientPublic } from '../models/PatientModel'

type LightEncounter = Pick<EncounterModel, 'template' | 'ehr_adapter' | 'patient' | 'report'>

// The session/auth lifecycle as one explicit value instead of several independently-mutable
// flags. Only sessionActions.ts assigns this.
export type SessionState =
    // Before Firebase's onIdTokenChanged has reported anything at all — distinct from
    // 'unauthenticated' so Login doesn't render for what might still turn out to be a
    // restored session (MD-1633). Settles into 'unauthenticated' or 'resolving' once it has.
    | { tag: 'initializing' }
    | { tag: 'unauthenticated' }
    // `cameFrom` distinguishes an interactive sign-in (stay on Login) from a restored session (show a spinner).
    | { tag: 'resolving'; cameFrom: 'login' | 'restoredSession' }
    | { tag: 'active'; user: UserModel }
    // Reachable from 'active' any time a request 403s as disabled (see api.ts); no transition back out.
    // `user` can be null if that happens before /me has resolved one.
    | { tag: 'accountDisabled'; user: UserModel | null }
    // Terminal until the next sign-in attempt.
    | { tag: 'accountNotFound' }

/** For an exhaustive `switch (session.tag)`: a branch that reaches here only compiles if every
 * tag was already handled above it, so adding a new tag forces every such switch to be revisited
 * instead of silently falling through a `default` or an if/else chain's last `else`. */
export const assertNever = (value: never): never => {
    throw new Error(`Unhandled SessionState tag: ${JSON.stringify(value)}`)
}

export interface AppState {
    preSelectedTemplateId: string | null
    preSelectedPatientId: string | null
    isPatientPinned: boolean
    isTemplatePinned: boolean

    template: TemplateModel | null
    patient: PatientPublic | null
    /** True only when the user explicitly opted out of selecting a patient, as opposed to not
     * having picked one yet — both leave `patient` null but need different UI treatment. */
    isNoPatientSelected: boolean

    /** The session/auth lifecycle. Written only by sessionActions.ts. */
    session: SessionState
    /** Mirrors `session`'s user for the ~20 files that don't care about lifecycle stage. */
    userModel: UserModel | null
    orgModel: OrganizationModel | null
    isIntegration: boolean
    integrationOrigin: string | null
    isAdapterConnected: boolean
    encounterHistory: Record<string, LightEncounter[]>
    encounterHistoryIndex: Record<string, number>

    isTopPanelLoading: boolean
    templates: Record<string, TemplateModel>
    favoriteTemplateIds: string[]
    templatesLoading: boolean
    /** Navigation flag for templates (to show ProgressBar during heavy rendering) */
    isNavigatingToTemplate: boolean
    groups: GroupModel[]
    /** Distinguishes "no groups" from "groups not fetched yet" (sharing UI must not guess) */
    groupsLoaded: boolean
    /** True while any editable field in the app has focus (see useEditingTracker) */
    isEditing: boolean
    /** True once the Intercom messenger is booted and can be opened (see useIntercom) */
    isIntercomReady: boolean
    intercomUnreadCount: number
}

// Everything belonging to the signed-in user's session — the account's own data and the
// launch parameters the embedding EHR hands over with it — as opposed to device and
// environment state. Signing out ends the session, so all of it goes and the next login
// starts from here (MD-1418). The launch params are the one part a reset can be asked to
// carry over, and only while no account has been established yet — see
// resetUserScopedState (MD-1583).
//
// A factory, not a constant: the collections would otherwise be the same instances every
// time, so a sign-out could restore a container a previous session had written into.
const createLaunchParamsInitialState = () =>
    ({
        preSelectedTemplateId: null,
        preSelectedPatientId: null,
        isPatientPinned: false,
        isTemplatePinned: false,
    }) satisfies Partial<AppState>

/** The launch parameters as they stand right now — what a reset preserves when there is no
 * previous session for them to leak out of (see resetUserScopedState). */
const launchParamsOf = (state: AppState) =>
    ({
        preSelectedTemplateId: state.preSelectedTemplateId,
        preSelectedPatientId: state.preSelectedPatientId,
        isPatientPinned: state.isPatientPinned,
        isTemplatePinned: state.isTemplatePinned,
    }) satisfies Partial<AppState>

const createUserScopedInitialState = () =>
    ({
        ...createLaunchParamsInitialState(),

        template: null,
        patient: null,
        isNoPatientSelected: false,

        session: { tag: 'initializing' },
        userModel: null,
        orgModel: null,
        isAdapterConnected: false,
        encounterHistory: {},
        encounterHistoryIndex: {},
        isTopPanelLoading: false,
        templates: {},
        favoriteTemplateIds: [],
        templatesLoading: true,
        isNavigatingToTemplate: false,
        groups: [],
        groupsLoaded: false,
        isEditing: false,
    }) satisfies Partial<AppState>

export const useAppStore = create<AppState>((_set) => ({
    ...createUserScopedInitialState(),
    isIntegration: false,
    integrationOrigin: null,
    // Left out of the reset on purpose: the widget itself is not shut down on sign-out, so
    // clearing the badge would hide that session rather than end it (MD-1418).
    isIntercomReady: false,
    intercomUnreadCount: 0,
}))

export const resetUserScopedState = (): void => {
    const state = useAppStore.getState()
    const { session } = state

    // A cold launch from an embedding EHR resolves Firebase's persisted auth asynchronously, so
    // onIdTokenChanged reports "no user" *after* useUrlParams has read the launch params off the
    // URL and stripped the query string. Treating that first null like a sign-out wiped the pins
    // the EHR had just handed over, and nothing could put them back — the URL was already gone,
    // so the user landed unpinned and could not submit to the EHR (MD-1583).
    //
    // The line that separates that from a real sign-out is not "was anyone signing in" but "did
    // an account ever become established in this app instance": only `userResolved` reaches
    // 'active', and `tokenAppeared` enters 'resolving' solely from initializing/unauthenticated/
    // accountNotFound (see sessionActions) — so an established session can never reach a
    // params-keeping tag again without passing through this reset first, which clears them on the
    // way. That's the invariant this reset exists to hold: no previous account's data may reach
    // the next login on a shared device (MD-1418).
    const keepLaunchParams = ((): boolean => {
        switch (session.tag) {
            // No account has been established: either nothing has begun yet, or a sign-in
            // began and never produced one — the /me grace retry gave up, or /me 404'd, both
            // of which sign out from 'resolving' (see useUserInit). Each leaves the user back
            // on Login with the EHR launch still in progress and the URL that carried the pins
            // long stripped, so clearing them here strands that launch exactly as MD-1583 did,
            // while there is still no previous account whose data could reach the next login.
            case 'initializing':
            case 'unauthenticated':
            case 'resolving':
            case 'accountNotFound':
                return true
            // Someone was signed in. Ending that session ends everything it carried (MD-1418).
            case 'active':
            case 'accountDisabled':
                return false
            default:
                return assertNever(session)
        }
    })()

    useAppStore.setState({
        ...createUserScopedInitialState(),
        ...(keepLaunchParams ? launchParamsOf(state) : createLaunchParamsInitialState()),
        // 'accountNotFound' survives its own trigger (the sign-out in useUserInit) so Login can render it.
        session: session.tag === 'accountNotFound' ? session : { tag: 'unauthenticated' },
    })
}
